import {Dealer, Dealers} from "../types/Dealers";
import mapboxgl from "mapbox-gl";
import MapboxToken from "../constants/MapboxToken";
import {FeatureCollection, Point} from "geojson";
import DefaultMarkerColor from "../constants/DefaultMarkerColor";
import ActiveMarkerColor from "../constants/ActiveMarkerColor";

/**
 * Initializes the Map
 * @param dealersGeoJSON
 */
export const initMap = (dealersGeoJSON: FeatureCollection<Point, Dealer>) => {

	// Setting the MapBox token
	mapboxgl.accessToken = MapboxToken;

	const zoom = window.innerWidth < 768 ? 2 : 3;
	const mapboxMap = new mapboxgl.Map({
		container: 'dealers-map',
		style: 'mapbox://styles/mapbox/streets-v9',
		zoom,
		center: [-100, 57],
		cooperativeGestures: true
	});

	// Add the navigation control to the map
	mapboxMap.addControl(new mapboxgl.NavigationControl());

	// Create and Add markers (Dealers) to the Map
	addMarkers(mapboxMap, dealersGeoJSON);

	return mapboxMap;
}

export const createDealersGeoJSON = (dealers: Dealers): FeatureCollection<Point, Dealer> => {

	const dealersGeoJSON: FeatureCollection<Point, Dealer> = {
		type: "FeatureCollection",
		features: []
	};

	dealers.forEach((dealer) => {
		if (!dealer.dealerLocation) {
			return;
		}

		dealersGeoJSON.features.push({
			type: "Feature",
			geometry: {
				type: "Point",
				coordinates: [dealer.dealerLocation.lng, dealer.dealerLocation.lat]
			},
			properties: {
				dealerId: dealer.dealerId,
				name: dealer.name,
				dealerLocation: dealer.dealerLocation,
				phoneNumber: dealer.phoneNumber,
				email: dealer.email
			}
		});
	});

	return dealersGeoJSON;
}

/**
 * Adds markers to the map
 * @param map
 * @param dealersGeoJSON
 */
export const addMarkers = (map: mapboxgl.Map, dealersGeoJSON: FeatureCollection<Point, Dealer>) => {

	let previouslyClickedMarker: HTMLElement | null = null;

	// Add the markers to the map (Dealers)
	dealersGeoJSON.features.forEach((dealer) => {

		// Check if the dealer has a location
		if (!dealer.properties.dealerLocation) {
			return;
		}

		// Create a popup for the marker
		const popup = new mapboxgl.Popup({
			className: 'dealers-map-popup'
		}).setHTML(
			`
			<div class="dealers-map-popup__content">
				<h3 class="dealers-map-popup__title">${dealer.properties.name}</h3>
				<p class="dealers-map-popup__address"><strong>Address:</strong> ${dealer.properties.dealerLocation.address}</p>
				<p class="dealers-map-popup__phone"><strong>Phone:</strong> ${dealer.properties.phoneNumber}</p>
				<p class="dealers-map-popup__email"><strong>Email:</strong> ${dealer.properties.email}</p>
			</div>
			`
		);

		// Create the marker
		const dealerMarker = new mapboxgl.Marker()
			.setLngLat([dealer.properties.dealerLocation.lng, dealer.properties.dealerLocation.lat])
			.setPopup(popup)
			.addTo(map);

		// Get the marker element
		const dealerMarkerEl = dealerMarker.getElement();

		// Add an ID to the marker to be able to find it later on the Card click
		dealerMarkerEl.id = `dealer-marker-${dealer.properties.dealerId}`;

		popup.on('close', () => {

			const dealerMarkerSVGPathEl = dealerMarkerEl.querySelector<SVGPathElement>('svg path');
			if (!dealerMarkerSVGPathEl) {
				return;
			}

			if (previouslyClickedMarker === null) {

				// Deactivate the previously active card
				const activeCard = document.querySelector('.dealer-card_active');
				if (activeCard) {
					activeCard.classList.remove('dealer-card_active');
				}

			}

			if (previouslyClickedMarker !== dealerMarkerEl) {
				// Change the marker color back to default
				dealerMarkerSVGPathEl.style.fill = '#3FB1CE';
			}

		});

		// Add a click event to the marker
		dealerMarkerEl.addEventListener('click', (event) => {

			if (!dealer.properties) {
				return;
			}

			if (!dealer.properties.dealerLocation) {
				return;
			}

			const dealersContainer = document.getElementById("dealers-list");
			if (!dealersContainer) {
				return;
			}

			const dealerMarkerSVGPathEl = dealerMarkerEl.querySelector<SVGPathElement>('svg path');
			if (!dealerMarkerSVGPathEl) {
				return;
			}

			// Get the related dealer card (by ID)
			const dealerCard = document.getElementById(`dealer-card-${dealer.properties.dealerId}`);
			if (!dealerCard) {
				return;
			}

			if (previouslyClickedMarker !== null && previouslyClickedMarker !== dealerMarkerEl) {

				// Deactivate the previously active card
				const activeCard = document.querySelector('.dealer-card_active');
				if (activeCard) {
					activeCard.classList.remove('dealer-card_active');
				}

				// Make all other markers inactive
				const allMarkers = document.querySelectorAll('.mapboxgl-marker');
				allMarkers.forEach((marker) => {
					const markerPath = marker.querySelector<SVGPathElement>('svg path');
					if (!markerPath) {
						return;
					}
					markerPath.style.fill = DefaultMarkerColor;
				});

			}

			if (previouslyClickedMarker !== dealerMarkerEl) {

				if (!dealerCard.classList.contains('dealer-card_active')) {

					// Make the related card active/highlighted
					dealerCard.classList.add('dealer-card_active');

					// Scroll to the dealer card
					const dealersContainerRect = dealersContainer.getBoundingClientRect();
					const dealerCardRect = dealerCard.getBoundingClientRect();
					dealersContainer.scrollTo({
						top: dealerCardRect.top - dealersContainerRect.top + dealersContainer.scrollTop,
						behavior: 'smooth'
					});

				}

				// Change the marker color to active
				dealerMarkerSVGPathEl.style.fill = ActiveMarkerColor;

				// Update the previously clicked marker
				previouslyClickedMarker = dealerMarkerEl;

			}

		});
	});
}
